import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User } from '@/types/auth'
import { useAuth } from '@/hooks/useAuth'
import { secureLogger } from '@/lib/secure-logger'

// Using custom notification system instead of react-hot-toast
const toast = {
  success: (message: string, options?: { icon?: string; duration?: number }) => {
    // Non-blocking notification - silent for now
    // Will be replaced with proper toast notification system
    secureLogger.info('Toast success', { message, options })
  },
  error: (message: string) => {
    // Non-blocking notification - log error securely
    // Will be replaced with proper toast notification system
    secureLogger.error('Toast error', { message })
  }
}

interface ImpersonationContextType {
  // Core state
  isImpersonating: boolean
  impersonatedEmployeeId: string | null

  // Actions
  startImpersonation: (targetEmployeeId: string | number) => Promise<boolean>
  stopImpersonation: () => Promise<void>
  canImpersonate: boolean

  // Get the current impersonation header value
  getImpersonationEmployeeId: () => string | null
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined)

interface ImpersonationProviderProps {
  children: ReactNode
}

// Only admin users can impersonate other users
const ADMIN_ROLES: User['role'][] = ['admin']

export function ImpersonationProvider({ children }: ImpersonationProviderProps) {
  const { user: currentUser, isLoading: isAuthLoading } = useAuth()
  const [isImpersonating, setIsImpersonating] = useState(false)
  const [impersonatedEmployeeId, setImpersonatedEmployeeId] = useState<string | null>(null)

  // Check if current user can impersonate others (check both current role and originalRole)
  const canImpersonate = currentUser ? (ADMIN_ROLES.includes(currentUser.role) || (currentUser.originalRole && ADMIN_ROLES.includes(currentUser.originalRole))) : false

  // Check for existing impersonation state on mount
  useEffect(() => {
    const savedEmployeeId = localStorage.getItem('impersonation-employee-id')

    // Wait for auth to finish loading before making decisions
    if (isAuthLoading) {
      return
    }

    if (savedEmployeeId && currentUser && canImpersonate) {
      setIsImpersonating(true)
      setImpersonatedEmployeeId(savedEmployeeId)
    } else if (savedEmployeeId && currentUser && !canImpersonate) {
      // Only clear if user is loaded but is NOT admin (lost admin role)
      localStorage.removeItem('impersonation-employee-id')
    } else if (savedEmployeeId && !currentUser) {
      // Only clear if we're sure there's no user (logged out)
      localStorage.removeItem('impersonation-employee-id')
    }
  }, [currentUser, canImpersonate, isAuthLoading])

  const startImpersonation = async (targetEmployeeId: string | number): Promise<boolean> => {
    if (!currentUser || !canImpersonate) {
      toast.error('You do not have permission to impersonate users')
      return false
    }

    // Convert to string and validate
    const employeeIdStr = String(targetEmployeeId || '').trim()

    if (!employeeIdStr) {
      toast.error('Invalid employee ID')
      return false
    }

    try {
      // Store just the employeeId in localStorage
      localStorage.setItem('impersonation-employee-id', employeeIdStr)

      setIsImpersonating(true)
      setImpersonatedEmployeeId(employeeIdStr)

      toast.success(`Now impersonating employee ${employeeIdStr}`, {
        icon: '🎭',
        duration: 3000
      })

      // Reload to apply impersonation to all API calls
      setTimeout(() => {
        window.location.reload()
      }, 500)

      return true
    } catch (error) {
      secureLogger.error('Error starting impersonation', { error: error instanceof Error ? error.message : 'Unknown error' })
      toast.error('Failed to start impersonation')
      return false
    }
  }

  const stopImpersonation = async (): Promise<void> => {
    if (!isImpersonating) {
      return
    }

    try {
      // Clear impersonation state
      localStorage.removeItem('impersonation-employee-id')
      setIsImpersonating(false)
      setImpersonatedEmployeeId(null)

      toast.success(`Returned to admin view`, {
        icon: '',
        duration: 2000
      })

      // Refresh page to remove impersonation from API calls
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error) {
      secureLogger.error('Error stopping impersonation', { error: error instanceof Error ? error.message : 'Unknown error' })
      toast.error('Failed to stop impersonation')
    }
  }

  const getImpersonationEmployeeId = (): string | null => {
    return impersonatedEmployeeId
  }

  const value: ImpersonationContextType = {
    isImpersonating,
    impersonatedEmployeeId,
    startImpersonation,
    stopImpersonation,
    canImpersonate: canImpersonate || false,
    getImpersonationEmployeeId
  }

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext)
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider')
  }
  return context
}
